import router from '@/routes'
import Cookies from 'js-cookie'
import Avatar from '@/models/Avatar'
import API from '@/api'

export default {
  namespaced: true,
  state: {
    user: null,
    isAuthenticated: false,
    token: {
      access_token: null,
      expires_in: null,
      token_type: 'Bearer',
      uuid: null
    },
    verificationToken: {
      token: null,
      expiresAt: null
    }
  },

  getters: {
    getUserThumbAvatar(state) {
      return state.user
        ? state.user.avatar_permanent_thumb_url ||
            state.user.avatar_permanent_url
        : ''
    },
    getUserAvatar(state) {
      return state.user && state.user.avatar ? state.user.avatar.url : ''
    },

    getUserPhotoFront(state) {
      return state.user && state.user.photo_id && state.user.photo_id.front
        ? state.user.photo_id.front.url
        : ''
    },

    getUserHeadshot(state) {
      return state.user && state.user.photo_id && state.user.photo_id.headshot
        ? state.user.photo_id.headshot.url
        : ''
    },

    getUserUnreadCount(state) {
      return state.user ? state.user.unread_notifications : 0
    },

    getNeededVerification(state, getters) {
      if (!state.isAuthenticated || getters.isVerified) return
      if (!state.user.email_verified) {
        return {
          name: 'auth.verify-page',
          params: {
            id: 'email'
          }
        }
      } else if (!state.user.full_name || !state.user.phone_number) {
        return {
          name: 'auth.onboarding-profile-page'
        }
      } else if (!state.user.phone_number.verified) {
        return {
          name: 'auth.verify-page',
          params: {
            id: 'phone'
          }
        }
      } else {
        return {
          name: 'auth.onboarding-id-page'
        }
      }
    },

    hasCompleteProfile(state) {
      return state.user && state.user.full_name
    },

    hasVerificationIds(state, getters) {
      return !!getters.getUserPhotoFront && !!getters.getUserHeadshot
    },

    isIdPending(state, getters) {
      return (
        getters.hasVerificationIds &&
        !state.user.photo_id.verified &&
        !state.user.photo_id.declined
      )
    },

    isIdDeclined(state, getters) {
      return state.user.photo_id.declined
    },

    isIdVerified(state, getters) {
      return state.user.photo_id.verified
    },

    isVerified(state) {
      return (
        state.isAuthenticated &&
        state.user &&
        state.user.full_name &&
        state.user.email_verified &&
        state.user.phone_number_verified
      )
    },

    isPayoutEnabled(state) {
      return (
        state.user && state.user.payouts_enabled && state.user.charges_enabled
      )
    }
  },
  mutations: {
    /**
     * Set auth token and set
     * global authentication to true
     * @param {Object} token
     */
    setToken(state, token) {
      if (!token) return

      Cookies.set('AUTH_TOKEN', token)
      state.token = token
      state.isAuthenticated = true
    },

    /**
     * Clears auth token from state & storage
     * and set global authentication to false
     */
    clearToken(state) {
      Cookies.remove('AUTH_TOKEN')
      state.token = {
        access_token: null,
        expires_in: null,
        token_type: 'Bearer',
        uuid: null
      }
      state.isAuthenticated = false
    },

    clearGoogleToken(state) {
      if (
        window.gapi &&
        window.gapi.auth2 &&
        window.gapi.auth2.getAuthInstance()
      ) {
        window.gapi.auth2.getAuthInstance().disconnect()
      }
    },

    /**
     * Set and clear user
     */
    setUser(state, user) {
      state.user = user
    },

    clearUser(state) {
      state.user = null
    },

    /**
     * Set user avatar
     */
    setUserAvatar(state, avatar) {
      if (avatar instanceof Avatar) {
        state.user.avatar = avatar
      } else {
        state.user.avatar = new Avatar(avatar)
      }
    },

    /**
     * Set user photo ID front
     */
    setUserPhotoFront(state, avatar) {
      let image = avatar.photo_id_front
      if (image instanceof Avatar) {
        state.user.photo_id.front = image
      } else {
        state.user.photo_id.front = new Avatar(image)
      }
    },

    /**
     * Set user photo headshot
     */
    setUserHeadshot(state, avatar) {
      let image = avatar.headshot
      if (image instanceof Avatar) {
        state.user.photo_id.headshot = image
      } else {
        state.user.photo_id.headshot = new Avatar(image)
      }
    },

    /**
     * Set and clear verification token
     */
    setVerificationToken(state, token) {
      if (token) {
        state.verificationToken = token
      }
    },

    clearVerificationToken(state) {
      state.verificationToken = {
        token: null,
        expiresAt: null
      }
    },

    clearUnread(state) {
      if (!state.user) return
      state.user.unread_notifications = 0
    },

    addUnread(state) {
      if (!state.user) return
      state.user.unread_notifications = state.user.unread_notifications + 1
    },

    changeTimestamp(state) {
      state.timestamp = new Date().getTime()
    }
  },
  actions: {
    /**
     * Fetch and store auth token on local storage
     */
    init({ commit }) {
      commit('setToken', Cookies.getJSON('AUTH_TOKEN'))
    },

    async social({ commit }, formData) {
      const { data } = await API.post('auth/social', formData)
      commit('clearGoogleToken')
      commit('setToken', data.data)
      commit('setUser', data.data.user)
    },

    async register({ commit }, formData) {
      const { data } = await API.post('auth/register', formData)
      commit('setToken', data.data)
      commit('setUser', data.data.user)
    },

    async login({ commit }, formData) {
      const { data } = await API.post('auth/login', formData)
      commit('setToken', data.data)
      commit('setUser', data.data.user)
    },

    async logout({ commit, dispatch }) {
      await API.post('auth/logout').then(() => {
        commit('clearToken')
        commit('clearUser')
        commit('clearVerificationToken')
        commit('clearGoogleToken')

        let label = 'You have logged out'
        if (router.currentRoute.name !== 'home') {
          router.push({
            name: 'home',
            params: {
              alert: {
                label: label
              }
            }
          })
        } else {
          dispatch('alert/addAlert', { label: label }, { root: true })
        }
      })
    },

    async getAuthenticatedUser({ commit, state }, ifPossibleOnly = false) {
      if (ifPossibleOnly && !state.token.access_token) return
      await API.get('auth/me')
        .then(res => {
          commit('setUser', res.data.data)
        })
        .catch(err => {
          if (err.response.status === 401) {
            commit('clearToken')
            commit('clearUser')
            commit('clearVerificationToken')
            commit('clearGoogleToken')

            if (!ifPossibleOnly) {
              router.replace({ name: 'auth.login-page' })
            }
          }
        })
    },

    async requestVerification({ commit }, formData) {
      await API.post('auth/verification/send', formData)
    },

    async verifyUser({ state, getters }, formData) {
      const { data } = await API.post('auth/verification/verify', formData)
      let to = formData.via.toLowerCase()
      switch (to) {
        case 'email':
          state.user.email_verified = data.data.email_verified
          break
        case 'phone_number':
          state.user.phone_number_verified = data.data.phone_number_verified
          break
      }

      let route = getters.getNeededVerification
      if (route) {
        router.push(route)
      } else {
        router.push({
          name: 'auth.onboarding-id-page'
        })
      }
    },

    async getVerificationToken({ commit }, formData) {
      const { data } = await API.post(
        'auth/account/verification-token',
        formData
      )
      commit('setVerificationToken', data.data)
    },

    async requestChangeEmail({ commit }, formData) {
      await API.post('auth/change/email', formData)
    },

    async verifyChangeEmail({ state, commit }, formData) {
      const { data } = await API.post('auth/change/email/verify', formData)
      commit('clearVerificationToken')
      state.user.email = data.data.email
    },

    async requestForgotPassword({ commit }, formData) {
      await API.post('auth/forgot-password', formData)
    },

    async checkForgotPassword({ commit }, formData) {
      await API.post('auth/reset-password/check', formData)
    },

    async verifyForgotPassword({ state, commit }, formData) {
      await API.post('auth/reset-password', formData)
      commit('clearVerificationToken')
    },

    async requestChangePhone({ commit }, formData) {
      await API.post('auth/change/phone-number', formData)
    },

    async verifyChangePhone({ state, commit }, formData) {
      const { data } = await API.post(
        'auth/change/phone-number/verify',
        formData
      )
      commit('clearVerificationToken')
      state.user.phone_number = data.data.phone_number
    },

    async checkPassword({ commit }, formData) {
      await API.post('auth/check-password', formData)
    },

    async setPassword({ state, commit }, formData) {
      await API.post('auth/password', formData)
      state.user.has_password = true
    },

    async updatePassword({ commit }, formData) {
      await API.post('auth/change/password', formData)
    },

    async updateUser({ commit }, formData) {
      const { data } = await API.put('auth/profile', formData)
      commit('setUser', data.data)
    },

    async changeAvatar({ commit }, formData) {
      const { data } = await API.post('auth/profile/avatar', formData)
      commit('setUserAvatar', data.data)
    },

    async changePhoto({ commit }, formData) {
      const { data } = await API.post('stripe/account/documents', formData)
      if (formData.has('photo_id_front')) {
        commit('setUserPhotoFront', data.data)
      } else if (formData.has('headshot')) {
        commit('setUserHeadshot', data.data)
      }
    },

    async deleteAccount({ commit }) {
      await API.delete('auth/account')
      commit('clearToken')
      commit('clearUser')
      commit('clearVerificationToken')
      commit('clearGoogleToken')
    }
  }
}
